import React from 'react';
import { Box, Typography } from '@mui/material';

import { useCurrentUser } from '../../auth/useCurrentUser';
import { colors } from '../../theme/tokens';

/**
 * Env name resolution — deliberately simple for Round 1:
 *  - `REACT_APP_ENVIRONMENT` if set (build-time, e.g. `dev01`, `prod`)
 *  - else `NODE_ENV` (`development` / `production`)
 *
 * The display label collapses verbose values: `development` → `dev`
 * (short, fits alongside email in the 28px strip). Everything else is
 * passed through. Upstream is responsible for canonical names like
 * `dev01`, `qa`, `prod`.
 *
 * Prod-or-not is derived from the resolved name. Anything containing
 * 'prod' (case-insensitive) counts — covers `prod`, `production`,
 * `perf-prod`, etc. — everything else is treated as a non-prod env.
 */
const resolveEnv = (): { label: string; isProd: boolean } => {
  const raw =
    (process.env.REACT_APP_ENVIRONMENT && process.env.REACT_APP_ENVIRONMENT.trim()) ||
    process.env.NODE_ENV ||
    'development';
  const lower = raw.toLowerCase();
  const label = lower === 'development' ? 'dev' : lower;
  return { label, isProd: /prod/i.test(raw) };
};

const STRIP_HEIGHT = 28;

/**
 * Persistent env strip at the very top of `/admin/*`. Per Designer A:
 *  - amber in non-prod
 *  - red in prod
 *  - label: "{ENV} · Admin View · {user.email}"
 *
 * Sticky (position: sticky, top: 0) rather than fixed so it participates
 * in normal flex flow but sticks through scroll.
 */
const EnvStrip: React.FC = () => {
  const { email } = useCurrentUser();
  const { label, isProd } = resolveEnv();

  const bg = isProd ? colors.envProd : colors.envDev;
  const text = isProd ? colors.bgWhite : colors.warningText;

  return (
    <Box
      role="banner"
      aria-label={`Admin view · ${label}`}
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: (t) => t.zIndex.appBar + 1,
        height: STRIP_HEIGHT,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        backgroundColor: bg,
        color: text,
        px: 2,
        borderBottom: `1px solid rgba(0,0,0,0.08)`,
      }}
    >
      {/* Env pill — bold uppercase, visually the loudest. */}
      <Box
        component="span"
        sx={{
          fontFamily: 'Manrope, sans-serif',
          fontWeight: 800,
          fontSize: 11,
          letterSpacing: '0.8px',
          textTransform: 'uppercase',
          border: `1px solid ${isProd ? 'rgba(255,255,255,0.5)' : 'rgba(146,64,14,0.35)'}`,
          borderRadius: '4px',
          px: 0.75,
          py: 0.125,
          lineHeight: 1.4,
        }}
      >
        {label}
      </Box>
      {/* Scope label — medium weight, regular casing for readability. */}
      <Box
        component="span"
        sx={{
          fontFamily: 'Manrope, sans-serif',
          fontWeight: 600,
          fontSize: 12,
        }}
      >
        Admin View
      </Box>
      {email && (
        <>
          <Box component="span" sx={{ opacity: 0.4, fontSize: 12 }}>
            ·
          </Box>
          {/* Email kept lowercase / mixed-case — never force-uppercase. */}
          <Box
            component="span"
            sx={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 500,
              fontSize: 12,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: { xs: 120, sm: 240, md: 400 },
            }}
          >
            {email}
          </Box>
        </>
      )}
    </Box>
  );
};

export default EnvStrip;
